import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { City, Program } from '@/new-transport/_models/transport-map.model';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { JsonPipe } from '@angular/common';
import { VehicleTransport } from '@/new-transport/_models/transport.model';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { ParticipantsTabComponent } from '@/new-transport/_shared/city-map/city/city-modal/participants-tab/participants-tab.component';
import { deepClone } from '@/_utils/deep-clone';
import { ParticipantsTabAction } from './participants-tab/participants-tab.model';
import { calculateParticipantsAmountFromPrograms } from '@/new-transport/_utils/calculate-participants-amount-from-programs/calculate-participants-amount-from-programs';

export interface CityModalData {
	currentCity: City;
	programs: Program[];
	programsInRoute: Program[];
	vehicleInUse: VehicleTransport;
	vehicleInUseFreeSeats: number;
}

@Component({
	selector: 'app-city-modal',
	standalone: true,
	imports: [
		MatDialogModule,
		MatButtonModule,
		MatExpansionModule,
		JsonPipe,
		CdkScrollableModule,
		ParticipantsTabComponent,
	],
	templateUrl: './city-modal.component.html',
	styleUrl: './city-modal.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CityModalComponent {
	readonly dialogData = signal<CityModalData>(inject(MAT_DIALOG_DATA));
	readonly ParticipantsTabAction = ParticipantsTabAction;

	takenPrograms = signal<Program[]>(deepClone(this.dialogData().programsInRoute));
	programsToTake = signal(deepClone(this.dialogData().programs));

	programs = signal({
		outsideVehicle: this.programsToTake(),
		taken: this.takenPrograms(),
	});

	takenParticipantsAmount = computed(() => calculateParticipantsAmountFromPrograms(this.programs().taken));

	private readonly defaultPrograms = signal(deepClone(this.programs())).asReadonly();

	private readonly dialogRef = inject(MatDialogRef<CityModalComponent>);
	private readonly cdr = inject(ChangeDetectorRef);

	save(): void {
		this.dialogRef.close({
			taken: this.programs().taken,
			outsideVehicle: this.programs().outsideVehicle,
		});
	}

	updatePrograms($event: { taken: Program[]; outsideVehicle: Program[] }): void {
		this.programs.set({ taken: $event.taken, outsideVehicle: $event.outsideVehicle });
		this.cdr.detectChanges();
	}

	close(): void {
		this.programs.set(deepClone(this.defaultPrograms()));
		this.dialogRef.close(null);
	}
}
